import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
    DollarSign,
    Building,
    Receipt,
    AlertCircle,
    CheckCircle,
} from "lucide-react";

function Dashboard() {
    const { user } = useAuth();
    const [resumen, setResumen] = useState([]);
    const [gastosRecientes, setGastosRecientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [mesActual, setMesActual] = useState(new Date().getMonth() + 1);
    const [anioActual, setAnioActual] = useState(new Date().getFullYear());

    const cargarDatos = useCallback(async () => {
        try {
            setLoading(true);

            // Cargar resumen de pagos
            const resumenResponse = await axios.get("/api/pagos/resumen", {
                params: { mes: mesActual, anio: anioActual },
            });
            setResumen(resumenResponse.data);

            // Cargar gastos recientes
            const gastosResponse = await axios.get("/api/pagos/detalle-gastos", {
                params: { mes: mesActual, anio: anioActual },
            });
            setGastosRecientes(gastosResponse.data.slice(0, 5));
        } catch (error) {
            console.error("Error al cargar datos del dashboard:", error);
        } finally {
            setLoading(false);
        }
    }, [mesActual, anioActual]);

    useEffect(() => {
        cargarDatos();
    }, [cargarDatos]);

    const formatearMoneda = (cantidad) => {
        return new Intl.NumberFormat("es-ES", {
            style: "currency",
            currency: "EUR",
        }).format(cantidad);
    };

    const formatearFecha = (fecha) => {
        return new Date(fecha).toLocaleDateString("es-ES");
    };

    const meses = [
        "Enero",
        "Febrero",
        "Marzo",
        "Abril",
        "Mayo",
        "Junio",
        "Julio",
        "Agosto",
        "Septiembre",
        "Octubre",
        "Noviembre",
        "Diciembre",
    ];

    // Calcular totales generales
    const totales = resumen.reduce(
        (acc, habitacion) => {
            acc.totalImporte += parseFloat(habitacion.total_importe || 0);
            acc.importePagado += parseFloat(habitacion.importe_pagado || 0);
            acc.importePendiente += parseFloat(habitacion.importe_pendiente || 0);
            return acc;
        },
        { totalImporte: 0, importePagado: 0, importePendiente: 0 }
    );

    if (loading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="container">
            <div className="dashboard-header">
                <h1>Dashboard</h1>
                <p>Bienvenido/a, {user?.nombre}</p>

                <div className="filtros">
                    <select
                        value={mesActual}
                        onChange={(e) => setMesActual(parseInt(e.target.value))}
                        className="form-select"
                    >
                        {meses.map((mes, index) => (
                            <option key={index} value={index + 1}>
                                {mes}
                            </option>
                        ))}
                    </select>

                    <select
                        value={anioActual}
                        onChange={(e) => setAnioActual(parseInt(e.target.value))}
                        className="form-select"
                    >
                        <option value={2023}>2023</option>
                        <option value={2024}>2024</option>
                        <option value={2025}>2025</option>
                        <option value={2026}>2026</option>
                        <option value={2027}>2027</option>
                        <option value={2028}>2028</option>
                    </select>
                </div>
            </div>

            {/* Tarjetas de resumen */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon total">
                        <DollarSign size={24} />
                    </div>
                    <div className="stat-content">
                        <h3>Total Gastos</h3>
                        <p className="stat-value">
                            {formatearMoneda(totales.totalImporte)}
                        </p>
                        <span className="stat-period">
                            {meses[mesActual - 1]} {anioActual}
                        </span>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon pagado">
                        <CheckCircle size={24} />
                    </div>
                    <div className="stat-content">
                        <h3>Pagado</h3>
                        <p className="stat-value">
                            {formatearMoneda(totales.importePagado)}
                        </p>
                        <span className="stat-period">
                            {totales.totalImporte > 0
                                ? Math.round(
                                        (totales.importePagado / totales.totalImporte) * 100
                                  )
                                : 0}
                            % completado
                        </span>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon pendiente">
                        <AlertCircle size={24} />
                    </div>
                    <div className="stat-content">
                        <h3>Pendiente</h3>
                        <p className="stat-value">
                            {formatearMoneda(totales.importePendiente)}
                        </p>
                        <span className="stat-period">Por cobrar</span>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon habitaciones">
                        <Building size={24} />
                    </div>
                    <div className="stat-content">
                        <h3>Habitaciones</h3>
                        <p className="stat-value">{resumen.length}</p>
                        <span className="stat-period">Activas</span>
                    </div>
                </div>
            </div>

            <div className="dashboard-content">
                {/* Resumen por habitaciones */}
                <div className="card">
                    <h2>Resumen por Habitaciones</h2>
                    <div className="table-container">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Habitación</th>
                                    <th>Total Gastos</th>
                                    <th>Pagado</th>
                                    <th>Pendiente</th>
                                    <th>Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {resumen.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan="5"
                                            style={{
                                                textAlign: "center",
                                                padding: "40px",
                                            }}
                                        >
                                            No hay gastos para {meses[mesActual - 1]} {anioActual}
                                        </td>
                                    </tr>
                                ) : (
                                    resumen.map((habitacion) => {
                                        const porcentajePagado =
                                            habitacion.total_importe > 0
                                                ? (habitacion.importe_pagado /
                                                        habitacion.total_importe) *
                                                  100
                                                : 0;

                                        return (
                                            <tr key={habitacion.habitacion_id}>
                                                <td>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "8px",
                                                        }}
                                                    >
                                                        <Building size={16} />
                                                        {habitacion.habitacion_numero}
                                                    </div>
                                                </td>
                                                <td>
                                                    {formatearMoneda(habitacion.total_importe || 0)}
                                                </td>
                                                <td style={{ color: "#10b981" }}>
                                                    {formatearMoneda(habitacion.importe_pagado || 0)}
                                                </td>
                                                <td style={{ color: "#ef4444" }}>
                                                    {formatearMoneda(habitacion.importe_pendiente || 0)}
                                                </td>
                                                <td>
                                                    <span
                                                        className={`badge ${
                                                            porcentajePagado === 100
                                                                ? "badge-success"
                                                                : porcentajePagado > 0
                                                                ? "badge-warning"
                                                                : "badge-danger"
                                                        }`}
                                                    >
                                                        {porcentajePagado === 100
                                                            ? "Completo"
                                                            : porcentajePagado > 0
                                                            ? "Parcial"
                                                            : "Pendiente"}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Gastos recientes */}
                <div className="card">
                    <h2>Gastos Recientes</h2>
                    <div className="gastos-recientes">
                        {gastosRecientes.length === 0 ? (
                            <div style={{ textAlign: "center", padding: "40px" }}>
                                <Receipt
                                    size={48}
                                    style={{
                                        color: "#d1d5db",
                                        marginBottom: "16px",
                                    }}
                                />
                                <p style={{ color: "#6b7280" }}>No hay gastos recientes</p>
                            </div>
                        ) : (
                            gastosRecientes.map((gasto) => (
                                <div key={gasto.id} className="gasto-item">
                                    <div className="gasto-info">
                                        <h4>{gasto.concepto}</h4>
                                        <p>
                                            {gasto.habitacion_numero} •{" "}
                                            {formatearFecha(gasto.fecha_creacion)}
                                        </p>
                                    </div>
                                    <div className="gasto-amount">
                                        <span className="amount">
                                            {formatearMoneda(gasto.importe_total)}
                                        </span>
                                        <span
                                            className={`badge ${
                                                gasto.estado_pago === "pagado"
                                                    ? "badge-success"
                                                    : "badge-warning"
                                            }`}
                                        >
                                            {gasto.estado_pago === "pagado" ? "Pagado" : "Pendiente"}
                                        </span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            <style jsx>{`
                .dashboard-header {
                    margin-bottom: 32px;
                }

                .dashboard-header h1 {
                    font-size: 32px;
                    font-weight: 700;
                    color: #1f2937;
                    margin-bottom: 8px;
                }

                .dashboard-header p {
                    color: #6b7280;
                    font-size: 16px;
                    margin-bottom: 24px;
                }

                .filtros {
                    display: flex;
                    gap: 12px;
                }

                .filtros .form-select {
                    width: auto;
                    min-width: 120px;
                }

                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: 20px;
                    margin-bottom: 32px;
                }

                .stat-card {
                    background: white;
                    padding: 24px;
                    border-radius: 12px;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }

                .stat-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                }

                .stat-icon.total {
                    background: linear-gradient(135deg, #3b82f6, #1d4ed8);
                }
                .stat-icon.pagado {
                    background: linear-gradient(135deg, #10b981, #059669);
                }
                .stat-icon.pendiente {
                    background: linear-gradient(135deg, #f59e0b, #d97706);
                }
                .stat-icon.habitaciones {
                    background: linear-gradient(135deg, #8b5cf6, #7c3aed);
                }

                .stat-content h3 {
                    font-size: 14px;
                    font-weight: 500;
                    color: #6b7280;
                    margin-bottom: 4px;
                }

                .stat-value {
                    font-size: 24px;
                    font-weight: 700;
                    color: #1f2937;
                    margin-bottom: 4px;
                }

                .stat-period {
                    font-size: 12px;
                    color: #9ca3af;
                }

                .dashboard-content {
                    display: grid;
                    gap: 24px;
                }

                .table-container {
                    overflow-x: auto;
                }

                .gastos-recientes {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                .gasto-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 16px;
                    background: #f9fafb;
                    border-radius: 8px;
                    border: 1px solid #e5e7eb;
                }

                .gasto-info h4 {
                    font-weight: 500;
                    color: #1f2937;
                    margin-bottom: 4px;
                }

                .gasto-info p {
                    font-size: 14px;
                    color: #6b7280;
                }

                .gasto-amount {
                    text-align: right;
                }

                .amount {
                    display: block;
                    font-weight: 600;
                    color: #1f2937;
                    margin-bottom: 4px;
                }

                @media (max-width: 768px) {
                    .stats-grid {
                        grid-template-columns: 1fr;
                    }

                    .filtros {
                        flex-direction: column;
                    }

                    .gasto-item {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 12px;
                    }

                    .gasto-amount {
                        text-align: left;
                        width: 100%;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                    }
                }
            `}</style>
        </div>
    );
}

export default Dashboard;
